/**
 * The force-update kill switch.
 *
 * Once a build is on a store it cannot be recalled, so this screen is the only way to stop
 * an old one talking to the API after a breaking change or a security fix. That makes it
 * the most dangerous screen in the console: one number, raised carelessly, locks every
 * customer out of the app until they visit a store.
 *
 * Three things follow from that, and they are the whole design:
 *
 *   One row at a time.  Four rows exist (customer/vendor × Android/iOS) and they are armed
 *                       independently, because store rollouts move at different speeds. But
 *                       editing them side by side is how you block iOS while meaning to
 *                       block Android, so exactly one is on screen and named at the top.
 *
 *   Say it in words.    "minimum 140, latest 160" makes the reader remember which way the
 *                       comparison runs. The panels say "builds below 140 are blocked"
 *                       instead, because getting that backwards is the failure mode.
 *
 *   Test, don't model.  The tester calls the real public endpoint — the same URL a phone
 *                       hits on its splash screen — and renders what came back. A local
 *                       re-implementation of the rule would be a second copy that drifts,
 *                       and an operator about to lock out the userbase deserves the truth
 *                       rather than this screen's opinion of it.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Phone,
  PlayCircle,
  Save,
  ShieldAlert,
  Smartphone,
  Wrench,
} from 'lucide-react';
import { enum_param, use_url_state } from '@/hooks/use-url-state';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { DetailPanel, Field, FieldList, PanelSkeleton } from '@/components/detail/detail-panel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { Label } from '@/components/ui/label';
import { notify } from '@/components/ui/toaster';
import { ConfirmModal } from '@/features/vendors/components/reason-modal';
import { cn } from '@/lib/utils';
import { date_time } from '@/lib/format';
import { use_app_configs, use_preview_config, use_update_app_config } from '@/features/app-config/hooks';
import {
  APP_LABEL,
  PLATFORM_LABEL,
  STATUS_CLASS,
  STATUS_LABEL,
  config_key,
  gate_sentence,
  is_armed,
  predict_status,
  type AppClient,
  type AppConfig,
  type AppPlatform,
  type PublicAppConfig,
  type UpdateAppConfigInput,
} from '@/features/app-config/types';

const APPS = ['customer', 'vendor'] as const;
type AppTab = (typeof APPS)[number];
const TAB_TO_APP: Record<AppTab, AppClient> = { customer: 'CUSTOMER', vendor: 'VENDOR' };

const PLATFORMS: AppPlatform[] = ['ANDROID', 'IOS'];

/** The editable fields, as strings — a half-typed number is not a number yet. */
type Draft = {
  minimum_build: string;
  latest_build: string;
  latest_version: string;
  store_url: string;
  update_title: string;
  update_message: string;
  optional_update_title: string;
  optional_update_message: string;
  is_maintenance: boolean;
  maintenance_title: string;
  maintenance_message: string;
  support_email: string;
  support_phone: string;
  support_whatsapp: string;
};

function draft_of(c: AppConfig): Draft {
  return {
    minimum_build: String(c.minimum_build),
    latest_build: String(c.latest_build),
    latest_version: c.latest_version,
    store_url: c.store_url,
    update_title: c.update_title,
    update_message: c.update_message,
    optional_update_title: c.optional_update_title,
    optional_update_message: c.optional_update_message,
    is_maintenance: c.is_maintenance,
    maintenance_title: c.maintenance_title,
    maintenance_message: c.maintenance_message,
    support_email: c.support_email ?? '',
    support_phone: c.support_phone ?? '',
    support_whatsapp: c.support_whatsapp ?? '',
  };
}

/**
 * Only what actually moved.
 *
 * A PATCH carrying every field would overwrite copy the operator never opened, and would
 * make "who changed this message" unanswerable. Blank contact fields are sent as null
 * rather than empty strings, because clearing one is a real intent the API models.
 */
function changed_fields(c: AppConfig, d: Draft): UpdateAppConfigInput {
  const out: UpdateAppConfigInput = {};
  const min = Number(d.minimum_build);
  const latest = Number(d.latest_build);
  if (Number.isFinite(min) && min !== c.minimum_build) out.minimum_build = min;
  if (Number.isFinite(latest) && latest !== c.latest_build) out.latest_build = latest;
  if (d.latest_version !== c.latest_version) out.latest_version = d.latest_version;
  if (d.store_url !== c.store_url) out.store_url = d.store_url;
  if (d.update_title !== c.update_title) out.update_title = d.update_title;
  if (d.update_message !== c.update_message) out.update_message = d.update_message;
  if (d.optional_update_title !== c.optional_update_title) out.optional_update_title = d.optional_update_title;
  if (d.optional_update_message !== c.optional_update_message)
    out.optional_update_message = d.optional_update_message;
  if (d.is_maintenance !== c.is_maintenance) out.is_maintenance = d.is_maintenance;
  if (d.maintenance_title !== c.maintenance_title) out.maintenance_title = d.maintenance_title;
  if (d.maintenance_message !== c.maintenance_message) out.maintenance_message = d.maintenance_message;

  const contact = (next: string, current: string | null) => (next.trim() === '' ? null : next.trim()) !== current;
  const as_null = (v: string) => (v.trim() === '' ? null : v.trim());
  if (contact(d.support_email, c.support_email)) out.support_email = as_null(d.support_email);
  if (contact(d.support_phone, c.support_phone)) out.support_phone = as_null(d.support_phone);
  if (contact(d.support_whatsapp, c.support_whatsapp)) out.support_whatsapp = as_null(d.support_whatsapp);
  return out;
}

/**
 * The two edits the API refuses, caught here so the operator reads a sentence about their
 * app rather than a validation error about a field.
 *
 * The server enforces these regardless — this is a courtesy, not the guard.
 */
function local_objection(d: Draft): string | null {
  const min = Number(d.minimum_build);
  const latest = Number(d.latest_build);
  if (!Number.isFinite(min) || min < 1) return 'The minimum build must be a whole number of 1 or more.';
  if (!Number.isFinite(latest) || latest < 1) return 'The latest build must be a whole number of 1 or more.';
  if (min > latest) {
    return `A minimum of ${min} above the latest build of ${latest} would block everyone, including people already on the newest version.`;
  }
  if (min > 1 && d.store_url.trim() === '') {
    return 'Set the store link first. Blocking people without one gives them a dialog whose only button goes nowhere.';
  }
  return null;
}

export function AppConfigPage() {
  const [tab_raw, set_tab] = use_url_state('tab', enum_param(APPS));
  const tab: AppTab = tab_raw || 'customer';
  const [platform, set_platform] = useState<AppPlatform>('ANDROID');

  const { data, isLoading, error, refetch } = use_app_configs();
  const save = use_update_app_config();

  const app = TAB_TO_APP[tab];
  const config = useMemo(
    () => data?.find((c) => c.app === app && c.platform === platform),
    [data, app, platform],
  );

  const [draft, set_draft] = useState<Draft | null>(null);
  const [confirming, set_confirming] = useState(false);

  /*
   * Reload the form when the selected row changes or the server sends a new version of it.
   * Keyed on the row identity plus its updated_at, so a successful save re-syncs the form
   * to what was actually stored rather than leaving the operator looking at their own
   * input as if it were the truth.
   */
  const sync_key = config ? `${config_key(config.app, config.platform)}:${config.updated_at}` : 'none';
  useEffect(() => {
    if (config) set_draft(draft_of(config));
  }, [sync_key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <DashboardLayout title="App config">
        <ErrorState
          title="Could not load the app config"
          description="Nothing has been changed. Try again."
          action={
            <Button size="sm" variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      </DashboardLayout>
    );
  }

  const pending = config && draft ? changed_fields(config, draft) : {};
  const dirty = Object.keys(pending).length > 0;
  const objection = draft ? local_objection(draft) : null;

  /** Raising the floor is the one edit that takes people out of the app. */
  const arming =
    config && draft && Number(draft.minimum_build) > config.minimum_build && Number(draft.minimum_build) > 1;

  async function commit() {
    if (!config || !draft) return;
    try {
      await save.mutateAsync({ app: config.app, platform: config.platform, input: pending });
      set_confirming(false);
      const min = Number(draft.minimum_build);
      notify.success(
        min > 1
          ? `${APP_LABEL[config.app]} on ${PLATFORM_LABEL[config.platform]}: builds below ${min} are now blocked`
          : `${APP_LABEL[config.app]} on ${PLATFORM_LABEL[config.platform]} saved`,
        { description: 'Apps pick this up on their next cold start, within about a minute.' },
      );
    } catch (e) {
      set_confirming(false);
      notify.error(e instanceof Error ? e.message : 'Could not save the config.');
    }
  }

  function attempt_save() {
    if (objection) return;
    if (arming) return set_confirming(true);
    void commit();
  }

  return (
    <DashboardLayout
      title="App config"
      subtitle="Force update, maintenance mode and support details for the mobile apps"
      tabs={
        <PageTabs
          tabs={APPS.map((a) => ({ value: a, label: APP_LABEL[TAB_TO_APP[a]] }))}
          current={tab}
          on_change={(v) => set_tab(v as AppTab)}
        />
      }
      actions={
        <Button
          onClick={attempt_save}
          disabled={!dirty || !!objection || save.isPending}
          data-testid="app-config-save"
        >
          {save.isPending ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-1 h-4 w-4" />
          )}
          Save changes
        </Button>
      }
    >
      <div className="grid gap-gutter">
        <PlatformPicker
          current={platform}
          on_change={set_platform}
          rows={(data ?? []).filter((c) => c.app === app)}
        />

        {isLoading || !config || !draft ? (
          <PanelSkeleton rows={6} />
        ) : (
          <>
            {objection && (
              <p
                className="flex items-start gap-2 rounded-panel border border-destructive bg-card p-3 text-data text-destructive"
                data-testid="app-config-objection"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                {objection}
              </p>
            )}

            <div className="grid items-start gap-gutter lg:grid-cols-[1.15fr_1fr]">
              <div className="grid gap-gutter">
                <GatePanel config={config} draft={draft} set_draft={set_draft} />
                <MessagesPanel draft={draft} set_draft={set_draft} />
                <MaintenancePanel draft={draft} set_draft={set_draft} />
                <SupportPanel draft={draft} set_draft={set_draft} />
              </div>

              <div className="grid gap-gutter">
                <TesterPanel app={app} platform={platform} dirty={dirty} />
                <StatePanel config={config} />
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmModal
        open={confirming}
        title="Block everyone below this build?"
        description={
          config && draft
            ? `On ${APP_LABEL[app]} / ${PLATFORM_LABEL[platform]}, anyone on a build below ${draft.minimum_build} will be stopped at the splash screen and sent to the store. Only raise this once ${draft.latest_version} is at 100% store rollout — a staged rollout means some of them cannot download it yet.`
            : ''
        }
        confirm_label="Block them"
        loading={save.isPending}
        on_confirm={() => void commit()}
        on_cancel={() => set_confirming(false)}
      />
    </DashboardLayout>
  );
}

/* ------------------------------- platform ------------------------------- */

/**
 * Which of the two rows is being edited, with each one's state on its own button.
 *
 * The armed/quiet word sits on the button rather than only inside the panel below, so an
 * operator can see that Android is blocking while iOS is not without clicking between them
 * — which is the actual question during a staged rollout.
 */
function PlatformPicker({
  current,
  on_change,
  rows,
}: {
  current: AppPlatform;
  on_change: (p: AppPlatform) => void;
  rows: AppConfig[];
}) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Platform">
      {PLATFORMS.map((p) => {
        const row = rows.find((r) => r.platform === p);
        const armed = row ? is_armed(row) : false;
        const active = p === current;
        return (
          <button
            key={p}
            onClick={() => on_change(p)}
            aria-pressed={active}
            data-testid={`platform-${p.toLowerCase()}`}
            className={cn(
              'flex min-w-[13rem] items-center gap-3 rounded-panel border p-3 text-left transition-colors',
              active ? 'border-primary bg-card shadow-panel' : 'border-border bg-card/60 hover:border-primary/50',
            )}
          >
            <Smartphone className={cn('h-4 w-4', active ? 'text-primary' : 'text-muted-foreground')} />
            <span className="grid">
              <span className="text-data font-semibold">{PLATFORM_LABEL[p]}</span>
              <span className={cn('text-micro', armed ? 'text-destructive' : 'text-muted-foreground')}>
                {row ? (armed ? `blocking below ${row.minimum_build}` : 'blocking nobody') : '—'}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* --------------------------------- fields -------------------------------- */

function TextField({
  id,
  label,
  value,
  on_change,
  hint,
  placeholder,
  wide,
}: {
  id: string;
  label: string;
  value: string;
  on_change: (v: string) => void;
  hint?: string;
  placeholder?: string;
  wide?: boolean;
}) {
  return (
    <div className={cn('grid gap-1.5', wide && 'sm:col-span-2')}>
      <Label htmlFor={id}>{label}</Label>
      <input
        id={id}
        data-testid={id}
        className="console-field w-full"
        value={value}
        placeholder={placeholder}
        onChange={(e) => on_change(e.target.value)}
      />
      {hint && <p className="text-micro text-muted-foreground">{hint}</p>}
    </div>
  );
}

function NumberField({
  id,
  label,
  value,
  on_change,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  on_change: (v: string) => void;
  hint?: string;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <input
        id={id}
        data-testid={id}
        className="console-field w-40 tabular"
        value={value}
        inputMode="numeric"
        onChange={(e) => on_change(e.target.value.replace(/[^\d]/g, ''))}
      />
      {hint && <p className="text-micro text-muted-foreground">{hint}</p>}
    </div>
  );
}

type DraftSetter = React.Dispatch<React.SetStateAction<Draft | null>>;
const patch = (set_draft: DraftSetter) => (part: Partial<Draft>) =>
  set_draft((d) => (d ? { ...d, ...part } : d));

/* ---------------------------------- gate --------------------------------- */

function GatePanel({
  config,
  draft,
  set_draft,
}: {
  config: AppConfig;
  draft: Draft;
  set_draft: DraftSetter;
}) {
  const edit = patch(set_draft);
  const min = Number(draft.minimum_build);
  const latest = Number(draft.latest_build);

  return (
    <DetailPanel
      title="Version gate"
      icon={ShieldAlert}
      description="Which builds may run, and where to send the ones that may not"
      test_id="app-config-gate"
    >
      <div className="grid gap-4">
        <p className="max-w-prose text-data text-muted-foreground">
          These are <strong>build numbers</strong>, not version names — Android{' '}
          <code className="text-micro">versionCode</code>, iOS{' '}
          <code className="text-micro">CFBundleVersion</code>. The app never compares versions
          itself; the server decides and the app does as it is told.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <NumberField
            id="minimum-build"
            label="Block below this build"
            value={draft.minimum_build}
            on_change={(v) => edit({ minimum_build: v })}
            hint={min > 1 ? 'These people cannot use the app at all.' : 'Set to 1 to block nobody.'}
          />
          <NumberField
            id="latest-build"
            label="Newest build on the store"
            value={draft.latest_build}
            on_change={(v) => edit({ latest_build: v })}
            hint={
              latest > min
                ? `Builds ${min}–${latest - 1} get a nudge they can skip.`
                : 'Nobody is nudged while this equals the minimum.'
            }
          />
          <TextField
            id="latest-version"
            label="Version name to show"
            value={draft.latest_version}
            on_change={(v) => edit({ latest_version: v })}
            hint="Display only, e.g. 1.6.0. Nothing is decided from it."
          />
          <TextField
            id="store-url"
            label="Store link"
            value={draft.store_url}
            on_change={(v) => edit({ store_url: v })}
            placeholder="https://play.google.com/store/apps/details?id=…"
            hint="Where the Update button goes."
          />
        </div>

        <p
          className={cn(
            'rounded-panel border p-3 text-data',
            min > 1 ? 'border-destructive text-destructive' : 'border-border text-muted-foreground',
          )}
          data-testid="gate-sentence"
        >
          {gate_sentence({ ...config, minimum_build: min || 1, latest_build: latest || 1 })}
        </p>
      </div>
    </DetailPanel>
  );
}

/* -------------------------------- messages ------------------------------- */

function MessagesPanel({ draft, set_draft }: { draft: Draft; set_draft: DraftSetter }) {
  const edit = patch(set_draft);
  return (
    <DetailPanel
      title="What the app says"
      icon={Smartphone}
      description="The two dialogs, in the user’s words"
      test_id="app-config-messages"
    >
      <div className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="update-title"
            label="Blocking dialog — title"
            value={draft.update_title}
            on_change={(v) => edit({ update_title: v })}
          />
          <TextField
            id="update-message"
            label="Blocking dialog — message"
            value={draft.update_message}
            on_change={(v) => edit({ update_message: v })}
            hint="Say why, if there is a reason worth giving."
          />
          <TextField
            id="optional-update-title"
            label="Nudge — title"
            value={draft.optional_update_title}
            on_change={(v) => edit({ optional_update_title: v })}
          />
          <TextField
            id="optional-update-message"
            label="Nudge — message"
            value={draft.optional_update_message}
            on_change={(v) => edit({ optional_update_message: v })}
          />
        </div>
      </div>
    </DetailPanel>
  );
}

/* ------------------------------ maintenance ------------------------------ */

function MaintenancePanel({ draft, set_draft }: { draft: Draft; set_draft: DraftSetter }) {
  const edit = patch(set_draft);
  return (
    <DetailPanel
      title="Maintenance mode"
      icon={Wrench}
      description="Hold everyone at the splash screen during planned downtime"
      test_id="app-config-maintenance"
    >
      <div className="grid gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-data font-semibold">Maintenance is</span>
          {[
            { on: false, label: 'Off' },
            { on: true, label: 'On' },
          ].map((opt) => (
            <button
              key={opt.label}
              onClick={() => edit({ is_maintenance: opt.on })}
              aria-pressed={draft.is_maintenance === opt.on}
              data-testid={`maintenance-${opt.label.toLowerCase()}`}
              className={cn(
                'rounded-control border px-3 py-1.5 text-data font-semibold transition-colors',
                draft.is_maintenance === opt.on
                  ? opt.on
                    ? 'border-warning bg-card text-warning'
                    : 'border-primary bg-card text-foreground'
                  : 'border-border text-muted-foreground hover:text-foreground',
              )}
            >
              {opt.label}
            </button>
          ))}
          {draft.is_maintenance && (
            <span className="text-micro text-warning">
              Every app on this platform is held at the splash screen.
            </span>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="maintenance-title"
            label="Title"
            value={draft.maintenance_title}
            on_change={(v) => edit({ maintenance_title: v })}
          />
          <TextField
            id="maintenance-message"
            label="Message"
            value={draft.maintenance_message}
            on_change={(v) => edit({ maintenance_message: v })}
            hint="Give a time if you have one — it halves the support tickets."
          />
        </div>
      </div>
    </DetailPanel>
  );
}

/* -------------------------------- support -------------------------------- */

function SupportPanel({ draft, set_draft }: { draft: Draft; set_draft: DraftSetter }) {
  const edit = patch(set_draft);
  return (
    <DetailPanel
      title="Support contacts"
      icon={Phone}
      description="Shown on the blocking screens, where nothing else in the app is reachable"
      test_id="app-config-support"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="support-email"
          label="Email"
          value={draft.support_email}
          on_change={(v) => edit({ support_email: v })}
          placeholder="help@petinfy.com"
        />
        <TextField
          id="support-phone"
          label="Phone"
          value={draft.support_phone}
          on_change={(v) => edit({ support_phone: v })}
        />
        <TextField
          id="support-whatsapp"
          label="WhatsApp"
          value={draft.support_whatsapp}
          on_change={(v) => edit({ support_whatsapp: v })}
          hint="Leave any of these empty to remove it from the app."
        />
      </div>
    </DetailPanel>
  );
}

/* --------------------------------- tester -------------------------------- */

/**
 * "What would build N be told?", answered by the server rather than by this page.
 *
 * This is the screen's reason to exist beyond a form. Raising a minimum is irreversible
 * from the user's side — they cannot go back to the old build — so being able to ask before
 * saving, and to confirm after, is worth more than any amount of explanatory copy.
 */
function TesterPanel({
  app,
  platform,
  dirty,
}: {
  app: AppClient;
  platform: AppPlatform;
  dirty: boolean;
}) {
  const [build, set_build] = useState('1');
  const preview = use_preview_config();
  const result: PublicAppConfig | undefined = preview.data;

  function run() {
    const n = Number(build);
    if (!Number.isFinite(n) || n < 1) return;
    preview.mutate({ app, platform, build: n });
  }

  /* A saved config is what the endpoint answers from, so an unsaved edit would mislead. */
  useEffect(() => {
    preview.reset();
  }, [app, platform]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DetailPanel
      title="Test it"
      icon={PlayCircle}
      description="Ask the live endpoint what a build would be told"
      test_id="app-config-tester"
    >
      <div className="grid gap-3">
        <p className="max-w-prose text-data text-muted-foreground">
          This calls the same public URL a phone hits on its splash screen, so the answer is
          what a real device would receive — not this page’s guess at it.
        </p>

        <div className="flex flex-wrap items-end gap-2">
          <div className="grid gap-1.5">
            <Label htmlFor="tester-build">Pretend to be build</Label>
            <input
              id="tester-build"
              data-testid="tester-build"
              className="console-field w-32 tabular"
              value={build}
              inputMode="numeric"
              onChange={(e) => set_build(e.target.value.replace(/[^\d]/g, ''))}
            />
          </div>
          <Button variant="outline" onClick={run} disabled={preview.isPending} data-testid="tester-run">
            {preview.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <PlayCircle className="mr-1 h-4 w-4" />
            )}
            Ask the server
          </Button>
        </div>

        {dirty && (
          <p className="text-micro text-warning" data-testid="tester-stale">
            You have unsaved changes. This answers from what is saved, not what is on screen.
          </p>
        )}

        {preview.isError && (
          <p className="text-data text-destructive" data-testid="tester-error">
            {preview.error instanceof Error ? preview.error.message : 'The request failed.'}
          </p>
        )}

        {result && <TesterResult result={result} />}
      </div>
    </DetailPanel>
  );
}

function TesterResult({ result }: { result: PublicAppConfig }) {
  const status = result.app.update_status;
  return (
    <div className="grid gap-3 rounded-panel border border-border p-3" data-testid="tester-result">
      <div className="flex items-center gap-2">
        {status === 'NONE' ? (
          <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
        ) : (
          <AlertTriangle className={cn('h-4 w-4', STATUS_CLASS[status])} />
        )}
        <span className={cn('text-data font-semibold', STATUS_CLASS[status])} data-testid="tester-status">
          {STATUS_LABEL[status]}
        </span>
        <Badge variant="outline">{status}</Badge>
      </div>

      {result.maintenance.is_active && (
        <p className="text-data text-warning">
          Maintenance is on — the app holds at the splash screen regardless of the build.
        </p>
      )}

      {status === 'NONE' ? (
        <p className="text-data text-muted-foreground">
          This build goes straight into the app with no dialog.
        </p>
      ) : (
        /* A mock of the dialog rather than a field list: the copy is the deliverable here,
           and reading it laid out as the user sees it is how typos get caught. */
        <div className="grid gap-1.5 rounded-control border border-border bg-muted/40 p-3">
          <p className="text-data font-semibold">{result.app.title}</p>
          <p className="text-data text-muted-foreground">{result.app.message}</p>
          <div className="flex items-center gap-2 pt-1">
            <span className="rounded-control bg-primary px-2.5 py-1 text-micro font-semibold text-primary-foreground">
              Update
            </span>
            {status === 'OPTIONAL' && <span className="text-micro text-muted-foreground">Later</span>}
          </div>
          {result.app.store_url === '' && (
            <p className="text-micro text-destructive">
              No store link — that Update button would go nowhere.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* --------------------------------- state --------------------------------- */

function StatePanel({ config }: { config: AppConfig }) {
  const armed = is_armed(config);
  return (
    <DetailPanel title="Saved state" icon={Smartphone} test_id="app-config-state">
      <FieldList>
        <Field
          label="Blocking"
          value={
            <span className={armed ? 'text-destructive' : 'text-muted-foreground'}>
              {armed ? `builds below ${config.minimum_build}` : 'nobody'}
            </span>
          }
        />
        <Field label="Newest build" value={`${config.latest_build} (${config.latest_version})`} />
        <Field
          label="Store link"
          value={config.store_url === '' ? <span className="text-muted-foreground">not set</span> : config.store_url}
        />
        <Field
          label="Maintenance"
          value={
            config.is_maintenance ? <span className="text-warning">on</span> : <span>off</span>
          }
        />
        <Field label="Last changed" value={date_time(config.updated_at)} />
        <Field
          label="What a fresh install sees"
          value={
            predict_status(config.latest_build, config.minimum_build, config.latest_build) === 'NONE'
              ? 'nothing — straight in'
              : 'a dialog'
          }
        />
      </FieldList>
    </DetailPanel>
  );
}
